<template>
  <v-dialog :value="value" max-width="760" persistent scrollable @input="$emit('input', $event)">
    <v-card class="ui-card">
      <div class="ui-card__header">
        <div>
          <h3 class="ui-section-title">Solicitar documentos</h3>
          <p class="ui-section-description">Escolha o destinatário, o prazo e os documentos solicitados.</p>
        </div>
        <v-btn icon small :disabled="saving" @click="close"><v-icon small>mdi-close</v-icon></v-btn>
      </div>

      <!-- v-card-text mantem a rolagem do dialogo scrollable -->
      <v-card-text class="ui-card__body pt-0 px-5 pb-5">
        <div class="ui-label">Destinatário</div>
        <v-radio-group v-model="target" row dense hide-details class="mt-0 mb-3">
          <v-radio label="Credor" value="creditor" />
          <v-radio label="Devedora" value="client" />
        </v-radio-group>

        <v-alert dense text type="info" class="mb-4">
          <template v-if="target === 'creditor'">
            Cada credor recebe um e-mail com a lista e o prazo e responde pelo portal, em "Documentos solicitados".
            Após o prazo a solicitação é encerrada e o envio deixa de ser possível. Não é preciso ter habilitação ou divergência.
          </template>
          <template v-else>
            A devedora recebe uma solicitação por credor, na tela "Documentos Solicitados" do painel dela,
            e envia os arquivos por lá. Os arquivos ficam visíveis na Central, junto do crédito.
          </template>
        </v-alert>

        <!-- Creditos sem pedido de hab/div: a solicitacao abre a analise de oficio deles. -->
        <v-alert v-if="officialLetterItems.length" dense text :type="habdivDeadlinePassed ? 'info' : 'warning'" icon="mdi-file-document-edit-outline" class="mb-4">
          <template v-if="habdivDeadlinePassed">
            <strong>{{ officialLetterItems.length }} crédito(s) sem pedido de hab/div</strong> terão a análise de ofício aberta
            automaticamente, com o valor do QGC e a análise pendente.
          </template>
          <template v-else>
            {{ officialLetterItems.length }} crédito(s) sem pedido de hab/div: os documentos serão solicitados, mas a análise de ofício
            só é aberta automaticamente depois do prazo de hab/div{{ habdivDeadlineLabel ? ` (${habdivDeadlineLabel})` : '' }}.
          </template>
        </v-alert>

        <div class="ui-label">Credores ({{ creditorGroups.length }})</div>
        <v-chip-group column class="mb-4">
          <v-chip v-for="group in creditorGroups" :key="group.key" small outlined>
            {{ group.creditor_name }}
            <span v-if="group.creditor_registration" class="ml-1 ui-muted">· {{ formatRegistration(group.creditor_registration) }}</span>
          </v-chip>
        </v-chip-group>

        <v-row dense align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="deadline"
              type="date"
              :min="today"
              label="Prazo para envio"
              outlined dense hide-details="auto"
              :rules="[(v) => !!v || 'Informe o prazo']"
            />
          </v-col>
          <v-col cols="12" md="7" class="d-flex align-center flex-wrap" style="gap: 6px;">
            <span class="ui-small ui-muted mr-1">Prazo em:</span>
            <v-chip v-for="days in [1, 5, 10, 15]" :key="`days-${days}`" small outlined
              :color="daysFromToday === days ? 'primary' : undefined" @click="setDeadlineDays(days)">
              {{ days }} {{ days === 1 ? 'dia' : 'dias' }}
            </v-chip>
          </v-col>
        </v-row>

        <v-select
          v-model="reminderRecurrence"
          :items="recurrenceOptions"
          item-text="text" item-value="value"
          label="Lembrete por e-mail"
          outlined dense hide-details="auto" class="mt-3"
        />

        <div class="ui-label mt-5">Documentos</div>
        <v-progress-linear v-if="loadingTypes" indeterminate class="mb-2" />
        <v-alert v-else-if="!documentTypes.length" dense text type="warning" class="mb-2">
          Nenhum tipo de documento cadastrado. Informe os documentos avulsos abaixo ou cadastre os tipos no menu Documentos.
        </v-alert>
        <v-checkbox
          v-for="type in documentTypes"
          :key="`type-${type.id}`"
          v-model="selectedTypeIds"
          :label="type.name"
          :value="type.id"
          hide-details class="mt-1"
        />

        <v-combobox
          v-model="customDocuments"
          label="Outros documentos (digite e pressione Enter)"
          multiple chips small-chips deletable-chips
          outlined dense class="mt-4" hide-details="auto"
          hint="Um item por documento. Ex.: Contrato nº 123, Notas fiscais de 2025"
          persistent-hint
        />

        <v-textarea
          v-model="message"
          :label="target === 'creditor' ? 'Orientações ao credor (vão no e-mail e no portal)' : 'Orientações à devedora (opcional)'"
          outlined dense rows="3" class="mt-4" hide-details="auto"
        />
      </v-card-text>

      <div class="ui-card__footer">
        <v-btn outlined :disabled="saving" @click="close">Cancelar</v-btn>
        <v-btn v-if="$allowed('client.document_requests')" color="primary" depressed :loading="saving" :disabled="!canSend" @click="send">
          Enviar solicitação
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

// Dialogo compartilhado pela lista e pelo detalhe da Central de Creditos.
// `credits` sao os creditos selecionados; credores repetidos viram uma unica solicitacao.
// Destinatario: credor (responde pelo portal) ou devedora (responde pelo painel dela).
const DEFAULT_DEADLINE_DAYS = 5;

function toDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default {
  name: "DebtorDocumentRequestDialog",

  props: {
    value: { type: Boolean, default: false },
    clientId: { type: [Number, String], default: null },
    credits: { type: Array, default: () => [] },
    defaultTarget: { type: String, default: "creditor" },
    // Prazo de hab/div do caso ({ date, source, passed }), vindo da Central.
    habdivDeadline: { type: Object, default: null },
  },

  data: () => ({
    loadingTypes: false,
    saving: false,
    target: "creditor",
    documentTypes: [],
    selectedTypeIds: [],
    customDocuments: [],
    deadline: null,
    reminderRecurrence: null,
    message: "",
    recurrenceOptions: [
      { text: "Sem lembrete", value: null },
      { text: "Diário", value: "daily" },
      { text: "Semanal", value: "weekly" },
      { text: "Quinzenal", value: "biweekly" },
      { text: "Mensal", value: "monthly" },
    ],
  }),

  computed: {
    today() {
      return toDateInput(new Date());
    },

    daysFromToday() {
      if (!this.deadline) return null;
      const start = new Date(`${this.today}T00:00:00`);
      const end = new Date(`${this.deadline}T00:00:00`);
      return Math.round((end - start) / (24 * 60 * 60 * 1000));
    },

    creditorGroups() {
      const groups = new Map();
      (this.credits || []).forEach((credit) => {
        const registration = String(credit.registration || "").replace(/\D/g, "");
        const key = registration || `name:${credit.creditor_name || ""}`;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            creditor_name: credit.creditor_name || "Credor sem nome",
            creditor_registration: registration || null,
            user_id: credit.user_id || null,
            user_event_id: this.firstEventId(credit),
            credit_keys: [],
          });
        }
        const group = groups.get(key);
        if (!group.user_id && credit.user_id) group.user_id = credit.user_id;
        if (!group.user_event_id) group.user_event_id = this.firstEventId(credit);
        if (credit.key) group.credit_keys.push(credit.key);
      });
      return [...groups.values()];
    },

    // Creditos so do QGC (sem hab/div) que ainda nao tem analise de oficio.
    officialLetterItems() {
      return (this.credits || [])
        .filter((credit) => credit.qgc_only && credit.qgc && credit.qgc.length)
        .filter((credit) => !(credit.sources || []).some((source) => source.kind === "official_letter"))
        .map((credit) => ({
          user_creditor_id: credit.qgc[0].id,
          credit_key: credit.key,
          amount: credit.values && credit.values.qgc !== null && credit.values.qgc !== undefined ? credit.values.qgc : credit.qgc[0].amount,
        }));
    },

    habdivDeadlinePassed() {
      return !!(this.habdivDeadline && this.habdivDeadline.passed);
    },

    habdivDeadlineLabel() {
      const date = this.habdivDeadline && this.habdivDeadline.date;
      if (!date) return "";
      const label = date.split("-").reverse().join("/");
      return this.habdivDeadline.source === "legal" ? `prazo legal em ${label}` : `prazo interno em ${label}`;
    },

    canSend() {
      return !!this.deadline
        && this.creditorGroups.length > 0
        && (this.selectedTypeIds.length > 0 || this.customDocuments.length > 0);
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
    },
  },

  methods: {
    firstEventId(credit) {
      const source = (credit.sources || []).find((item) => item.kind === "user_event" && item.reference_id);
      return source ? source.reference_id : null;
    },

    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value;
    },

    setDeadlineDays(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      this.deadline = toDateInput(date);
    },

    reset() {
      this.target = this.defaultTarget === "client" ? "client" : "creditor";
      this.selectedTypeIds = [];
      this.customDocuments = [];
      this.message = "";
      this.reminderRecurrence = null;
      this.setDeadlineDays(DEFAULT_DEADLINE_DAYS);
      this.loadTypes();
    },

    async loadTypes() {
      this.loadingTypes = true;
      try {
        const result = await axios.get("/api/admin/document-types", { params: { page: 1, limit: 1000 } });
        const content = result.data.content || {};
        const data = Array.isArray(content) ? content : (content.data || []);
        this.documentTypes = data.filter((type) => !type.deleted_at);
      } catch (error) {
        this.documentTypes = [];
      } finally {
        this.loadingTypes = false;
      }
    },

    close() {
      if (this.saving) return;
      this.$emit("input", false);
    },

    async send() {
      if (!this.canSend || !this.clientId) return;

      const who = this.target === "creditor" ? "credor(es)" : "credor(es), à devedora,";
      const proceed = await this.$dialog.confirm({
        title: "Solicitar documentos",
        text: `Enviar a solicitação referente a ${this.creditorGroups.length} ${who} com prazo até ${this.deadline.split("-").reverse().join("/")}?`,
        actions: { false: "Cancelar", true: "Enviar" },
      });
      if (!proceed) return;

      this.saving = true;
      try {
        const response = await axios.post(`/api/admin/clients/${this.clientId}/document-requests/creditors`, {
          target: this.target,
          deadline: this.deadline,
          reminder_recurrence: this.reminderRecurrence,
          document_type_ids: this.selectedTypeIds,
          custom_documents: this.customDocuments,
          message: this.message || null,
          creditors: this.creditorGroups.map((group) => ({
            user_id: group.user_id,
            creditor_name: group.creditor_name,
            creditor_registration: group.creditor_registration,
            user_event_id: group.user_event_id,
            credit_key: group.credit_keys.join("\n"),
          })),
          official_letters: this.officialLetterItems,
        });

        const created = Array.isArray(response.data.content) ? response.data.content : [];
        const count = created.length || this.creditorGroups.length;
        const letters = response.data.official_letters;
        const lettersText = letters && letters.opened ? ` ${letters.opened} análise(s) de ofício aberta(s).` : "";
        this.$dialog.notify.success(
          (this.target === "creditor"
            ? `${count} solicitação(ões) enviada(s) ao(s) credor(es).`
            : `${count} solicitação(ões) enviada(s) à devedora.`) + lettersText,
          { position: "top-right", timeout: 5000 }
        );
        this.$emit("sent", created);
        this.$emit("input", false);
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
